import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, logout as logoutService } from '../services/authService';
import { getProfileByUserId } from '../services/profileService';
import { setupInstantSync } from '../services/cartService';

// ============================================
// AUTH CONTEXT
// ============================================
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [cartRefreshTrigger, setCartRefreshTrigger] = useState(0);

  // Check for existing session on mount
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    // Prevent multiple simultaneous calls
    if (isCheckingUser) {
      console.log('User check already in progress, skipping...');
      return;
    }

    // Avoid calling Appwrite /account on every page load when there's no local hint
    // that a session exists. This prevents noisy 401 responses in DevTools when
    // no one is logged in. Trade-off: if a session exists but no local hint is set
    // (e.g., user logged in on another device), we won't detect it automatically.
    const hasLocalSessionHint = (
      localStorage.getItem('rememberMe') === 'true' ||
      sessionStorage.getItem('rememberMe') === 'false' ||
      sessionStorage.getItem('pendingRememberMe') !== null
    );

    if (!hasLocalSessionHint) {
      // No indication of a session; skip network call and set loading=false
      setLoading(false);
      return;
    }

    try {
      setIsCheckingUser(true);
      setLoading(true);
      const currentUser = await getCurrentUser();
      
      // Check if email is verified for the current user
      if (currentUser) {
        // OAuth users have emailVerification=true from Appwrite, trust that
        const isOAuthUser = currentUser.emailVerification === true;
        
        if (!isOAuthUser) {
          // For email/password users, check our custom verification collection
          const { isEmailVerified } = await import('../services/emailVerificationService');
          const verified = await isEmailVerified(currentUser.$id);
          
          if (!verified) {
            // User exists but email not verified - log them out
            console.warn('User email not verified, logging out...');
            await logoutService();
            setUser(null);
            setProfile(null);
            setLoading(false);
            setIsCheckingUser(false);
            return;
          }
        }
      }
      
      setUser(currentUser);

      // Fetch profile if user exists
      if (currentUser) {
        try {
          const userProfile = await getProfileByUserId(currentUser.$id);
          setProfile(userProfile);
        } catch (profileError) {
          console.error('Failed to fetch profile:', profileError);
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
    } catch (error) {
      console.error('User check failed:', error);
      // Clear hints if a serious error occurs (like 401)
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('sessionExpiry');
      sessionStorage.removeItem('rememberMe');
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
      setIsCheckingUser(false);
    }
  };

  // Setup instant sync when user is available
  useEffect(() => {
    if (user && user.$id) {
      setupInstantSync(user.$id);
    }
  }, [user]);

  const logout = async () => {
    try {
      await logoutService();
      setUser(null);
      setProfile(null);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const refreshProfile = React.useCallback(async () => {
    if (user) {
      try {
        const userProfile = await getProfileByUserId(user.$id);
        setProfile(userProfile);
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }
    }
  }, [user]);

  const refreshCart = () => {
    setCartRefreshTrigger(prev => prev + 1);
  };

  const value = {
    user,
    profile,
    setUser,
    setProfile,
    loading,
    logout,
    checkUser,
    refreshProfile,
    refreshCart,
    cartRefreshTrigger,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
